import React from 'react';
import { FiCheck, FiCircle, FiXCircle, FiMinusCircle } from 'react-icons/fi';

/**
 * StatusStepper - Vertical step indicator for the brokerage request lifecycle.
 *
 * Shows completed (green check), active (blue pulse), and future (gray dot) states.
 * Annotated with timestamps from timeline data when available.
 */

// The standard happy-path order of statuses
const STATUS_ORDER = [
  'SUBMITTED',
  'TRIAGE',
  'IN_REVIEW',
  'APPROVED',
  'STAGED',
  'SENT',
  'AWAITING',
  'CONSENTED',
  'SCHEDULED',
  'CONNECTED',
  'CLOSED',
];

// Terminal / shortcut statuses shown separately
const TERMINAL_STATUSES = ['EXPIRED', 'DECLINED', 'WITHDRAWN'];

// Friendly labels
const STATUS_LABELS = {
  SUBMITTED: 'Submitted',
  TRIAGE: 'Triage',
  IN_REVIEW: 'In Review',
  APPROVED: 'Approved',
  STAGED: 'Staged',
  SENT: 'Outreach Sent',
  AWAITING: 'Awaiting Consent',
  CONSENTED: 'Consented',
  SCHEDULED: 'Scheduled',
  CONNECTED: 'Connected',
  CLOSED: 'Closed',
  EXPIRED: 'Expired',
  DECLINED: 'Declined',
  WITHDRAWN: 'Withdrawn',
};

const StatusStepper = ({ currentStatus, timeline = [] }) => {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const isTerminal = TERMINAL_STATUSES.includes(currentStatus);

  // Build a map of status -> earliest timestamp from timeline
  const statusTimestamps = {};
  timeline.forEach((event) => {
    const status = event.after_state?.status || event.action;
    if (status && !statusTimestamps[status]) {
      statusTimestamps[status] = event.created_at || event.timestamp;
    }
  });

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const getStepState = (status, index) => {
    if (isTerminal) {
      // If we ended in a terminal state, mark everything up to where we got as completed
      // and mark the terminal status as active
      if (status === currentStatus) return 'active';
      if (currentIndex >= 0 && index <= currentIndex) return 'completed';
      // Check if this status appears in timeline (meaning we passed through it)
      if (statusTimestamps[status]) return 'completed';
      return 'future';
    }
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'active';
    return 'future';
  };

  return (
    <div className="space-y-1">
      {STATUS_ORDER.map((status, index) => {
        const state = getStepState(status, index);
        const timestamp = formatTimestamp(statusTimestamps[status]);

        return (
          <div key={status} className="flex items-start gap-2">
            {/* Step indicator */}
            <div className="flex flex-col items-center" style={{ minWidth: '20px' }}>
              {state === 'completed' ? (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <FiCheck className="w-3 h-3 text-white" />
                </div>
              ) : state === 'active' ? (
                <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center animate-pulse">
                  <FiCircle className="w-2.5 h-2.5 text-white fill-current" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                </div>
              )}
              {/* Connector line */}
              {index < STATUS_ORDER.length - 1 && (
                <div
                  className={`w-0.5 h-4 ${
                    state === 'completed' ? 'bg-green-400' :
                    state === 'active' ? 'bg-indigo-300' :
                    'bg-gray-200'
                  }`}
                />
              )}
            </div>

            {/* Label and timestamp */}
            <div className="flex-1 -mt-0.5 min-w-0">
              <p className={`text-xs font-medium leading-tight truncate ${
                state === 'completed' ? 'text-green-700' :
                state === 'active' ? 'text-indigo-700 font-semibold' :
                'text-gray-400'
              }`}>
                {STATUS_LABELS[status] || status}
              </p>
              {timestamp && state !== 'future' && (
                <p className="text-[10px] text-gray-400 leading-tight">{timestamp}</p>
              )}
            </div>
          </div>
        );
      })}

      {/* Terminal status indicator (if applicable) */}
      {isTerminal && !STATUS_ORDER.includes(currentStatus) && (
        <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-200">
          <div className="flex flex-col items-center" style={{ minWidth: '20px' }}>
            {currentStatus === 'DECLINED' ? (
              <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                <FiXCircle className="w-3 h-3 text-white" />
              </div>
            ) : currentStatus === 'WITHDRAWN' ? (
              <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center">
                <FiMinusCircle className="w-3 h-3 text-white" />
              </div>
            ) : (
              <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                <FiCircle className="w-3 h-3 text-white fill-current" />
              </div>
            )}
          </div>
          <div className="flex-1 -mt-0.5">
            <p className={`text-xs font-semibold leading-tight ${
              currentStatus === 'DECLINED' ? 'text-red-700' :
              currentStatus === 'WITHDRAWN' ? 'text-gray-700' :
              'text-orange-700'
            }`}>
              {STATUS_LABELS[currentStatus] || currentStatus}
            </p>
            {statusTimestamps[currentStatus] && (
              <p className="text-[10px] text-gray-400 leading-tight">
                {formatTimestamp(statusTimestamps[currentStatus])}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusStepper;
